import React, { useEffect, useState } from "react";
import { PricingPayload } from "../../../stores/PricingStore/Types";
import { priceLineFor } from "../priceLine";
import { pushServerDataLayer } from "../../../utils/analytics";
import { AxiosResponse, AxiosError } from "axios";
import { postRequest } from "../../../utils/httpRequest";
import {
  CoursePreviewResource,
  QuotationResource,
} from "../../../stores/CoursePreviewStore/Types";
import { CurrentUserResource } from "../../../stores/CurrentUserStore/Types";

interface Props {
  course: CoursePreviewResource;
  currentUser: CurrentUserResource;
  quotationResource: QuotationResource;
  showSigninSidebar: boolean;
  loadingQuotations: boolean;
  loadingEnrolmentInfo: boolean;
  mode?: string;
  enrolmentInfo: {
    existing_subscription: boolean;
    enrolment_id: number;
  };
  handleShowSignInSideBar: (redirectUrl?: string) => void;
  pricingState?: { kind: string; pricing?: PricingPayload };
}

const PurchaseNavbar: React.FC<Props> = ({
  course,
  currentUser,
  quotationResource,
  enrolmentInfo,
  mode,
  pricingState,
}) => {
  const {
    show_purchase_navbar,
    merchant_quotation_id,
    plan_slug,
    new_enrolment_url,
  } = quotationResource || {};

  const [enrolingUser, setEnrolingUser] = useState(false);
  const [showStickyEnrol, setShowStickyEnrol] = useState(false);
  const isUpcoming = mode === "upcoming";

  useEffect(() => {
    if (!isUpcoming && !show_purchase_navbar) {
      setShowStickyEnrol(false);
      return undefined;
    }

    const syncStickyEnrol = () => {
      const hero = document.querySelector(".facelift-course-preview .hero");

      setShowStickyEnrol(
        hero ? hero.getBoundingClientRect().bottom < 80 : false,
      );
    };

    syncStickyEnrol();
    window.addEventListener("scroll", syncStickyEnrol, { passive: true });
    window.addEventListener("resize", syncStickyEnrol);

    return () => {
      window.removeEventListener("scroll", syncStickyEnrol);
      window.removeEventListener("resize", syncStickyEnrol);
    };
  }, [isUpcoming, show_purchase_navbar]);

  // Tells the promo bar how far to move down. On a course page the price bar
  // owns the row under the nav, and the promotion sits beneath it — so this
  // publishes its height, and zero while it is still tucked out of sight.
  useEffect(() => {
    const bar = document.querySelector<HTMLElement>(".sticky-enrol");

    document.body.classList.add("course-has-promo-bar");
    document.documentElement.style.setProperty(
      "--enrol-h",
      showStickyEnrol && bar ? `${bar.offsetHeight}px` : "0px",
    );

    return () => {
      document.body.classList.remove("course-has-promo-bar");
      document.documentElement.style.setProperty("--enrol-h", "0px");
    };
  }, [showStickyEnrol]);

  const startCourse = (e) => {
    e.preventDefault();

    setEnrolingUser(true);

    return postRequest(`${new_enrolment_url}.json`, {
      plan_slug,
      merchant_quotation_id,
    })
      .then((response: AxiosResponse<{ url: string; datalayer?: any }>) => {
        pushServerDataLayer(response.data.datalayer);
        (window as any).location = response.data.url;
      })
      .catch((error: AxiosError<{ url: string }>) => {
        setEnrolingUser(false);

        (window as any).location = error.response.data.url;
      });
  };

  const renderActionButton = () => {
    if (enrolmentInfo?.enrolment_id) {
      return (
        <a
          href={`/enrolments/${enrolmentInfo.enrolment_id}`}
          className="btn btn-blue"
        >
          Continue
        </a>
      );
    } else if (currentUser.kind === "current-user") {
      if (enrolmentInfo?.existing_subscription || course.sample) {
        return (
          <a
            href="#"
            className={`btn btn-blue click-busy ${
              enrolingUser ? "loading" : ""
            }`}
            onClick={(e) => startCourse(e)}
          >
            <i className="student icon"></i>Start now
          </a>
        );
      }
    } else {
      return (
        <a
          href={`/courses/${course.slug}/pay`}
          className="btn btn-blue click-busy"
        >
          Enrol now
        </a>
      );
    }
  };

  if (isUpcoming) {
    return (
      <div className="facelift-course-preview">
        <aside
          className={`sticky-enrol upcoming-sticky${showStickyEnrol ? " show" : ""}`}
          aria-label="Launch signup"
          id="stickyEnrol"
        >
          <div className="inner">
            <div className="meta">
              <strong>{course?.name}</strong>
              <span className="coming">Coming soon</span>
            </div>
            <div className="ctas">
              <a className="btn btn-blue" href="#launch-signup">
                Be first to know
              </a>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  if (!show_purchase_navbar) return null;

  // The same figure as the hero card, so the bar never contradicts the page.
  const stickyPrice =
    course.sample || pricingState?.kind !== "ready" ? null : priceLineFor(pricingState.pricing);

  return (
    <div className="facelift-course-preview">
      <aside
        className={`sticky-enrol${showStickyEnrol ? " show" : ""}`}
        aria-label="Quick enrol"
        id="stickyEnrol"
      >
        <div className="inner">
          <div className="meta">
            <strong>{course?.name}</strong>
            <span className="stars" aria-label={`Rated ${course.rating.toFixed(1)} out of 5`}>
              ★★★★★ {course.rating.toFixed(1)}
            </span>
            {stickyPrice && (
              <span className="price">
                {stickyPrice.prefix && (
                  <span className="price-prefix">{stickyPrice.prefix} </span>
                )}
                <b>
                  <span className="price-symbol">{stickyPrice.symbol}</span>
                  {stickyPrice.amount}
                  {stickyPrice.code && <> {stickyPrice.code}</>}
                </b>
              </span>
            )}
          </div>
          <div className="ctas">
            {course.sample ? (
              <>{renderActionButton()}</>
            ) : (
              <a className="btn btn-blue" href={`/courses/${course.slug}/pay`}>
                Enrol now
              </a>
            )}
            {!course.sample && (
              <a className="btn btn-gift" href={`/gift?course=${course.slug}`}>
                Gift this
              </a>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default PurchaseNavbar;
